import {v4 as uuidv4} from "uuid";
import {faker}  from "@faker-js/faker";
import { getOrgApp } from "../helpers/get-orgApp";
import { regions } from "./regions";
import { SECURITY_BOT_ID } from "../../utils/app-constants.json";
import {
  SECURITY_BOT_INSIGHT_COUNT,
  COMPLIANCE_BOT_INSIGHT_COUNT,
} from "./constants";

import moment from "moment";

var updatedTime = moment();
updatedTime = moment.utc(updatedTime.subtract(1, "days"));

const regionCodes = regions.map((reg) => reg.RegionCode);

const severity = ["CRITICAL", "MEDIUM", "INFORMATIONAL", "HIGH", "LOW"];

const resources = [
  {
    key: "Account",
    label: "AWS Account",
  },
  {
    key: "SQS",
    label: "SQS",
  },
  {
    key: "S3",
    label: "S3 Bucket",
  },
  {
    key: "Rds",
    label: "RDS DBInstance",
  },
  {
    key: "EcsCluster",
    label: "ECS Cluster",
  },
  {
    key: "DocumentDbCluster",
    label: "Document DBCluster",
  },
  {
    key: "Topic",
    label: "SNS Topic",
  },
  {
    key: "KinesisStream",
    label: "Kinesis Stream",
  },
  {
    key: "CognitoUserPool",
    label: "Cognito UserPool",
  },
  {
    key: "CloudFrontDistribution",
    label: "CloudFront Distribution",
  },
  {
    key: "ApiGatewayRestApi",
    label: "API Gateway Rest API",
  },
  {
    key: "Lambda",
    label: "Lambda",
  },
  {
    key: "KmsAlias",
    label: "KMS Alias",
  },
  {
    key: "KMSKey",
    label: "KMS Key",
  },
  {
    key: "IAMUser",
    label: "IAM User",
  },
  {
    key: "IamAccessKey",
    label: "IAM Access Key",
  },
  {
    key: "GlueDataCatalog",
    label: "Glue Data Catalog",
  },
  {
    key: "CloudTrail Trail",
    label: "CloudTrail",
  },
  {
    key: "CloudFormationStack",
    label: "CloudFormation Stack",
  },
  {
    key: "DynamoDbTable",
    label: "DynamoDB Table",
  },
  {
    key: "Volume",
    label: "EC2 Volume",
  },
  {
    key: "Vpc",
    label: "EC2 VPC",
  },
  {
    key: "Instance",
    label: "EC2 Instance",
  },
  {
    key: "DocumentDbInstance",
    label: "DocumentDb Instance",
  },
  {
    key: "SecurityGroup",
    label: "Security Group",
  },
];

export const securityBotmessages = {
  Account: [
    "EMR account-level public security group access block should be enabled",
    "Amazon Macie should be enabled",
    "Amazon Detective should be enabled",
    "Amazon GuardDuty should be enabled",
    "Amazon IAM Access Analyzer should be enabled",
    "Security Hub should not have active high or critical severity findings from AWS services",
    "There should not be any server certificates stored in AWS IAM",
  ],
  ApiGatewayRestApi: [
    "API Gateway Rest API Stages should be protected by an AWS WAF Web ACL",
  ],
  CloudFormationStack: [
    "CloudFormation stacks should be monitored for changes",
  ],
  CloudFrontDistribution: ["Trusted signers should have key pairs"],
  "CloudTrail Trail": ["CloudTrail log file validation should be enabled"],
  CognitoUserPool: [
    "Cognito user pools should enforce multi factor authentication (MFA)",
  ],
  DocumentDbCluster: [
    "DocumentDB clusters should be configured for Multi-AZ",
    "DocumentDB clusters should have deletion protection enabled",
  ],
  DocumentDbInstance: [
    "DocumentDB instances should not be exposed to the public",
    "DocumentDB instances should have audit logging configured",
    "DocumentDB instances should be encrypted",
  ],
  DynamoDbTable: [
    "DynamoDB tables should have Time to Live (TTL) enabled",
    "DynamoDB tables should have Point-in-Time Recovery (PITR) enabled",
  ],
  Instance: ["EC2 instances should be protected by AWS Backup"],
  SecurityGroup: [
    "Security groups should not allow unrestricted access to all ports and protocols",
    "Security groups should not allow unrestricted MySQL or MariaDB database (TCP 3306) access",
  ],
  Volume: [
    "EBS volumes should be protected by AWS Backup",
    "EBS Volumes should be encrypted",
    "EBS Volumes should be configured to be deleted on termination",
    "EBS Volumes should be in an attached state",
  ],
  Vpc: [
    "Flow Logs should be enabled for all VPCs",
    "Consider deleting the Default VPC if unused",
  ],
  EcsCluster: [
    "ECS clusters should have a default cluster capacity provider strategy configured",
    "ECS clusters should have container insights enabled",
  ],
  GlueDataCatalog: [
    "AWS Glue data catalogs should enforce fine-grained access controls with a resource policy",
    "AWS Glue data catalogs should be configured to encrypt connection passwords",
    "AWS Glue data catalogs should be encrypted at rest",
  ],
  IamAccessKey: ["IAM Access Keys should be rotated every 90 days"],
  IAMUser: [
    "IAM users should not have attached in-line policies",
    "IAM users should not have attached managed policies",
    "IAM users should have Multi-Factor Authentication (MFA) enabled",
    "IAM users should have permissions boundaries attached",
  ],
  KinesisStream: [
    "Kinesis Data Streams should be encrypted",
    "Business-critical Kinesis Data Streams should have detailed monitoring configured",
  ],
  KmsAlias: ["KMS keys should not have public access"],
  KMSKey: ["KMS keys should have key rotation enabled"],
  Lambda: ["Lambda functions should be deleted after 30 days of no use"],
  Rds: [
    "RDS instances should not be publicly accessible",
    "RDS instances should have encrypted storage",
    "RDS instances should have performance insights enabled",
    "RDS instances that support IAM Authentication should use IAM Authentication",
    "RDS instances should be configured for high availability",
    "RDS instances that support Kerberos Authentication should be joined to a domain",
    "RDS instances should have deletion protection enabled",
  ],
  S3: [
    "S3 Buckets should have server access logging enabled",
    "S3 Buckets should have a bucket policy configured",
    "S3 Bucket Policies should not allow public access to the bucket",
    "S3 Buckets should have versioning enabled",
  ],
  Topic: [
    "SNS topics should not allow cross-account access",
    "SNS topics should not have public access",
    "SNS topics should not use HTTP subscriptions",
    "SNS topics should be encrypted",
  ],
  SQS: [
    "SQS messages should not be older than 80 percent of message retention",
  ],
};

export const complianceBotMessages = {
  Account: [
    "Account password policy for IAM users meets the default requirements enforced by AWS.",
    "AWS account is enabled to use multi-factor authentication (MFA) hardware device to sign in with root credentials.",
    "Root user of your AWS account requires multi-factor authentication for console sign-in.",
    "Emr Kerberos Enabled",
  ],
  ApiGatewayRestApi: [
    "API Gateway Rest API Stages should be protected by an AWS WAF Web ACL",
  ],
  CloudFrontDistribution: ["Trusted signers should have key pairs"],
  "CloudTrail Trail": ["CloudTrail log file validation should be enabled"],
  DynamoDbTable: [
    "Auto Scaling is enabled on your DynamoDB tables",
    "Provisioned DynamoDB throughput is approaching the maximum limit for your account.",
    "Operational Best Practices for Amazon DynamoDB",
    "Dynamodb Autoscaling Enabled",
    "Dynamodb In Backup Plan",
    "Dynamodb Pitr Enabled",
    "Dynamodb Throughput Limit Check",
  ],
  Instance: [
    "Latest versions of SSM & CloudWatch are installed on all windows instances.",
    "Latest versions of SSM & CloudWatch are installed on all linux instances.",
    "EC2 Instance Managed By Systems Manager",
    "EC2 Instance Detailed Monitoring Enabled",
    "EC2 Imdsv2 Check",
    "EC2 Ebs Encryption By Default",
    "EC2 Instance No Public Ip",
    "EC2 Managed Instance Association Compliance Status Check",
    "EC2 Managedinstance Patch Compliance Status Check",
    "EC2 Stopped Instance",
    "EC2 Volume Inuse Check",
  ],
  Volume: ["Ebs In Backup Plan", "EBS Volumes should be encrypted"],
  Vpc: [
    "Flow Logs should be enabled for all VPCs",
    "Consider deleting the Default VPC if unused",
  ],
  EcsCluster: [
    "ECS clusters should have a default cluster capacity provider strategy configured",
    "ECS clusters should have container insights enabled",
  ],
  IamAccessKey: [
    "Active access keys are rotated within 90 days.",
    "IAM Root user access key check.",
  ],
  IAMUser: [
    "IAM groups have at least one IAM user.",
    "IAM users are members of at least one IAM group.",
    "Default version of AWS Identity and Access Management (IAM) policies do not have administrator access.",
    "AWS Identity and Access Management users have multi-factor authentication (MFA) enabled.",
    "None of your IAM users have policies attached. IAM users must inherit permissions from IAM groups or roles.",
    "AWS Identity and Access Management (IAM) users have passwords or active access keys that have not been used within 90 days.",
    "AWS Multi-Factor Authentication (MFA) is enabled for all AWS Identity and Access Management (IAM) users that use a console password.",
  ],
  Lambda: [
    "Lambda Concurrency Check",
    "Lambda Function Public Access Prohibited",
    "Lambda Inside Vpc",
  ],
  Rds: [
    "DbInstance Backup Enabled",
    "Rds In Backup Plan",
    "Rds Multi Az Support",
  ],
  S3: [
    "S3 Account Level Public Access Blocks",
    "S3 Bucket Default Lock Enabled",
    "S3 Bucket Logging Enabled",
    "S3 Bucket Policy Grantee Check",
    "S3 Bucket Public Read/Write Prohibited",
    "S3 Bucket Replication Enabled",
    "S3 Bucket Server Side Encryption Enabled",
    "S3 Bucket Ssl Requests Only",
    "S3 Bucket Versioning Enabled",
    "S3 Bucket Replication Enabled",
    "Amazon S3 buckets have cross-region replication enabled",
    "S3 buckets have policies that require requests to use Secure Socket Layer (SSL).",
    "Amazon S3 bucket either has S3 default encryption enabled or that the S3 bucket policy explicitly denies put-object requests without server side encryption.",
  ],
};

const resourceIds = {
  Account: "arn:aws:account:{accountnumber}",
  ApiGatewayRestApi:
    "arn:aws:apigateway:{region}::/restapis/ravbiw7ty8/stages/stg1",
  CloudFormationStack:
    "arn:aws:cloudformation:{region}:{accountnumber}:stack/user-profile-stg1/arn:aws:cloudformation:{region}:{accountnumber}:stack/user-profile-stg1/4d16d620-a9e4-11e8-ae3a-069bf518e2a4",
  CloudFrontDistribution:
    "arn:aws:cloudfront::{accountnumber}:distribution/ELR01L5DGDPG8",
  "CloudTrail Trail":
    "arn:aws:cloudtrail:{region}:{accountnumber}:trail/MC-3-DefaultMonitoring-CloudTrail-QKVQRWFMVWLT",
  CognitoUserPool:
    "arn:aws:cognito-idp:{region}:{accountnumber}:userpool/us-east-2_AtD3gd5OZ",
  DocumentDbCluster:
    "arn:aws:rds:{region}:{accountnumber}:cluster:mc-8-rds-auroradb-rdscluster-11dchg8tao4vv",
  DocumentDbInstance:
    "arn:aws:rds:{region}:{accountnumber}:db:stg-mcstg1-use1-rds-db-dbinstnamev1",
  DynamoDbTable:
    "arn:aws:dynamodb:{region}:{accountnumber}:table/SchedulerTask_stg1",
  Instance: "arn:aws:ec2:{region}:{accountnumber}:instance/i-0a4b925498beabe45",
  SecurityGroup:
    "arn:aws:ec2:{region}:{accountnumber}:security-group/sg-0dbd51faeea9da9d6",
  Volume: "arn:aws:ec2:{region}:{accountnumber}/vol-03104e2f29f6041d2",
  Vpc: "arn:aws:ec2:{region}:{accountnumber}/vpc-096210f1ab8261442",
  EcsCluster: "arn:aws:ecs:{region}:{accountnumber}:cluster/ElectricEyeCluster",
  GlueDataCatalog: "arn:aws:glue:{region}:{accountnumber}:catalog",
  IAMUser: "arn:aws:iam::{accountnumber}:user/Sri-Staging",
  IamAccessKey: "arn:aws:iam::{accountnumber}:user/user-name",
  KinesisStream:
    "arn:aws:kinesis:{region}:{accountnumber}:stream/mc_test_event_stream",
  KmsAlias: "arn:aws:kms:{region}:{accountnumber}:alias/aws/rds",
  KMSKey:
    "arn:aws:kms:{region}:{accountnumber}:key/6e7b54a0-c3b9-4ce7-a6ae-de3f457197af",
  Lambda:
    "arn:aws:lambda:{region}:{accountnumber}:function:task-stg1-request_task",
  Rds: "arn:aws:rds:{region}:{accountnumber}:db:stg-mcstg1-use1-rds-db-dbinstnamev1",
  S3: "arn:aws:s3:::stg-mcstg1-use1-ct-sg-monitoringlogs",
  Topic:
    "arn:aws:sns:{region}:{accountnumber}:MontyCloud-API-Mapper-Json-Topic",
  SQS: "arn:aws:sqs:{region}:{accountnumber}:mc-stg1-org-discovery-queue",
};

const addInsight = async ({ botId, isFirstInsight, insightStatus }) => {
  let orgAppDetails = await getOrgApp();
  let selectedOrg = faker.helpers.arrayElement(orgAppDetails);
  let selectedDept = faker.helpers.arrayElement(selectedOrg.depts);
  let selectedDeptApp = faker.helpers.arrayElement(selectedDept.deptApps);
  let selectedDeptAccount = faker.helpers.arrayElement(
    selectedDept.deptAccounts
  );
  let _severity = faker.helpers.arrayElement(severity);

  let _status = insightStatus;

  const botMessages =
    botId === SECURITY_BOT_ID ? securityBotmessages : complianceBotMessages;

  const _resourcesForBots = Object.keys(botMessages);

  let _resource = faker.helpers.arrayElement(_resourcesForBots);
  if (isFirstInsight) {
    _resource = botId === SECURITY_BOT_ID ? "SecurityGroup" : "S3";
    _severity = "HIGH";
    _status = botId === SECURITY_BOT_ID ? "FAILED" : "NON_COMPLIANT";
  }

  const currentResource = resources.find((r) => r.key === _resource);
  let _title =
    botMessages[currentResource.key] &&
    faker.helpers.arrayElement(botMessages[currentResource.key]);
  if (isFirstInsight) {
    _title =
      botId === SECURITY_BOT_ID
        ? "Security groups should not allow unrestricted access to all ports and protocols"
        : "S3 Bucket Server Side Encryption Enabled";
  }

  const _regionCode = faker.helpers.arrayElement(regionCodes);

  const _accountNumber = selectedDeptAccount.accountNumber;

  const policyList = [
    [
      {
        PolicyId: uuidv4(),
        PolicyGroup:
          "Operational Best Practices for Management Governance Services",
      },
      {
        PolicyId: uuidv4(),
        PolicyGroup: "Operational Best Practices for HIPAA Security",
      },
      {
        PolicyId: uuidv4(),
        PolicyGroup: "Operational Best Practices for FedRAMP Low",
      },
    ],
    [
      {
        PolicyId: uuidv4(),
        PolicyGroup: "Operational Best Practices for NIST 800 171",
      },
      {
        PolicyId: uuidv4(),
        PolicyGroup: "Operational Best Practices for PCI DSS",
      },
      {
        PolicyId: uuidv4(),
        PolicyGroup: "Operational Best Practices for FedRAMP Low",
      },
    ],
  ];

  const _resourceId =
    resourceIds[currentResource.key] &&
    resourceIds[currentResource.key]
      .replace("{region}", _regionCode)
      .replace("{accountnumber}", _accountNumber);

  const id = uuidv4();
  return {
    Id: id,
    InsightId: id,
    Severity: _severity,
    InsightStatus: _status,
    ComplianceType: _status,
    Title: _title,
    Description: _title,
    ResourceId: _resourceId,
    ResourceType: currentResource.key,
    ResourceLabel: currentResource.label,
    UpdatedAt: updatedTime,
    ResultRecordedTime: updatedTime,
    ApplicationId: selectedDeptApp && selectedDeptApp.appId,
    DepartmentId: selectedDeptApp.appDeptId,
    AccountNumber: _accountNumber,
    RegionCode: _regionCode,
    Policies: faker.helpers.arrayElement(policyList),
  };
};

export const getAllBotInsights = async (botId) => {
  const count =
    botId === SECURITY_BOT_ID
      ? SECURITY_BOT_INSIGHT_COUNT
      : COMPLIANCE_BOT_INSIGHT_COUNT;
  let insights = [];
  const suppressedCount = botId === SECURITY_BOT_ID ? 20 : 0;
  const failedCount = Math.round(count / 3) - suppressedCount;
  const passedCount = count - failedCount - suppressedCount;
  for (var i = 0; i < failedCount; i++) {
    const insightStatus =
      botId === SECURITY_BOT_ID ? "FAILED" : "NON_COMPLIANT";
    const insight = await addInsight({
      botId,
      isFirstInsight: i === 0,
      insightStatus,
    });
    insights.push(insight);
  }
  for (var j = 0; j < passedCount; j++) {
    const insightStatus = botId === SECURITY_BOT_ID ? "PASSED" : "COMPLIANT";
    const insight = await addInsight({
      botId,
      insightStatus,
    });
    insights.push(insight);
  }
  if (botId === SECURITY_BOT_ID) {
    for (var k = 0; k < suppressedCount; k++) {
      const insightStatus = "SUPPRESSED";
      const insight = await addInsight({
        botId,
        insightStatus,
      });
      insights.push(insight);
    }
  }
  return insights;
};

export const getAllRemediations = () => {
  return [
    //compliance bot
    {
      ID: uuidv4(),
      Name: "Remove all Inbound and Outbound Rules",
      Description:
        "This removes all inbound and outbound rules for the specified security group which helps stop all traffic passing through this source.",
    },
    //security bot
    {
      ID: uuidv4(),
      Name: "Enable S3 Bucket Encryption",
      Description:
        "This helps encrypt all objects that are stored in the bucket. The objects are encrypted using server-side encryption with either Amazon S3-managed keys (SSE-S3) or customer master keys (CMKs) stored in AWS Key Management Service (AWS KMS).",
    },
  ];
};

export const getBotIndustryStandards = () => {
  return [
    { CompliantCount: 5, IndustryStandard: "HIPAA", NonCompliantCount: 1 },
    { CompliantCount: 4, IndustryStandard: "CIS", NonCompliantCount: 2 },
    { CompliantCount: 10, IndustryStandard: "FedRAMP", NonCompliantCount: 1 },
    { CompliantCount: 5, IndustryStandard: "NIST", NonCompliantCount: 1 },
    { CompliantCount: 2, IndustryStandard: "PCI", NonCompliantCount: 0 },
  ];
};
